import React, { useMemo, useState } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { CourseAdministrationTabNames, useCourseDropdown, useCourseList } from "../../../../query/hooks/QueryHooksCourseAdministration";
import { RenderCourseType, RenderLearningMethod, RenderSchool } from "../../../../utils/CommonGroupingItem";
import { RenderCourseStatus } from "../../../../utils/CommonStatusItems";
import { TABLE_DATE_FORMAT } from "../../../../utils/Constants";
import PermissionsGate from "../../../../utils/permissionGate";
import DataTableComponent from "../../../common/DataTableComponent";

const MODAL_FILTER_UID = "linkCoursesModal";

/**
  * Lists courses available to be linked to the current assembly.
  * Matches the student Courses & Programme assign modal pattern (+ to add, Save on parent tab).
  *
  * @param {Object} props
  * @param {Array<number|string>} [props.linkedCourseIds] - Course IDs already saved/linked (excluded from modal API).
  * @param {Array<number|string>} [props.pendingCourseIds] - Course IDs selected in this session (shown with minus in modal).
  * @param {Function} props.assignCourse - Adds a course to the parent's pending linked list.
  * @param {Function} props.removeCourse - Removes a course from the parent's pending linked list.
  * @param {boolean} [props.isLinking] - Whether a link request is in progress.
  * @returns {JSX.Element}
  */

const AvailableCourse = ({
    linkedCourseIds = [],
    pendingCourseIds = [],
    assignCourse,
    removeCourse,
    isLinking = false,
}) => {
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState({
        arr: [CourseAdministrationTabNames.Upcoming],
        checkObj: { [`${MODAL_FILTER_UID}status${CourseAdministrationTabNames.Upcoming}`]: true },
    });
    const [tableState, setTableState] = useState({
        page: 1,
        perPage: 10,
        sortKey: "start",
        sortOrder: "desc",
    });
    const [learningMethod, setLearningMethod] = useState({ arr: [], checkObj: {} });
    const [intake, setIntake] = useState({ arr: [], checkObj: {} });
    const [school, setSchool] = useState({ arr: [], checkObj: {} });
    const [courseType, setCourseType] = useState({ arr: [], checkObj: {} });

    const { data: courseDropdownRaw } = useCourseDropdown({
        viaStatus: status.arr,
    });

    const courseDropdown = useMemo(() => ({
        intake: courseDropdownRaw.yearsAndIntaks ?? courseDropdownRaw.intake ?? [],
        learning: courseDropdownRaw.intakeType ?? courseDropdownRaw.learning ?? [],
        school: courseDropdownRaw.schoolType ?? courseDropdownRaw.school ?? [],
        type: courseDropdownRaw.subjectTypeFilter ?? courseDropdownRaw.type ?? [],
    }), [courseDropdownRaw]);

    const pendingIds = useMemo(
        () => (pendingCourseIds || []).map((courseId) => Number(courseId)).filter(Boolean),
        [pendingCourseIds]
    );

    const excludedCourseIds = useMemo(
        () => (linkedCourseIds || []).map((courseId) => Number(courseId)).filter(Boolean),
        [linkedCourseIds]
    );

    const requestData = useMemo(() => ({
        pageNo: tableState.page,
        size: tableState.perPage,
        intake: intake.arr,
        viaLearnType: learningMethod.arr,
        key: tableState.sortKey,
        sort: tableState.sortOrder,
        viaStatus: status.arr,
        viaSearch: search,
        viaSchoolType: school.arr,
        viaSubjectType: courseType.arr,
        selectedCoursesFromList: excludedCourseIds,
        showing: [],
    }), [
        tableState,
        intake.arr,
        learningMethod.arr,
        status.arr,
        search,
        school.arr,
        courseType.arr,
        excludedCourseIds,
    ]);

    const { data: { courseList, totalRows }, isLoading, isError, error } = useCourseList(requestData);

    const emptyMessage = useMemo(() => {
        if (isLoading) return "";
        if (isError) return error?.response?.data?.message || "Unable to load courses.";
        if (!courseList.length) return "There are no records to show";
        return "";
    }, [isLoading, isError, error, courseList.length]);

    const isCoursePending = (courseId) => pendingIds.includes(Number(courseId));

    const handleToggleCourse = (course) => {
        if (isLinking) return;

        if (isCoursePending(course.id)) {
            removeCourse(Number(course.id));
            return;
        }

        assignCourse(course);
    };

    const columns = useMemo(() => [
        {
            name: "Name",
            selector: "name",
            sortField: "name",
            sortable: true,
            cell: (row) => (
                row.name ? (
                    <button
                        type="button"
                        className="as-text-blue curser feature-name btn btn-link p-0"
                        title={row.name}
                        onClick={() => handleToggleCourse(row)}
                    >
                        <span>{row.name}</span>
                    </button>
                ) : "-"
            ),
        },
        {
            name: "Start",
            selector: "start",
            sortField: "startdate",
            sortable: true,
            cell: (row) => (
                row.start ? (
                    <div className="dateTime">
                        <p className="right-space">{moment.unix(row.start).format(TABLE_DATE_FORMAT)}</p>
                    </div>
                ) : "-"
            ),
        },
        {
            name: "End",
            selector: "end",
            sortField: "enddate",
            sortable: true,
            cell: (row) => (
                row.end ? (
                    <div className="dateTime">
                        <p className="right-space">{moment.unix(row.end).format(TABLE_DATE_FORMAT)}</p>
                    </div>
                ) : "-"
            ),
        },
        {
            name: "Learning Method",
            selector: "type",
            sortField: "type",
            sortable: true,
            cell: (row) => (row.type ? RenderLearningMethod(row.type).html : "-"),
        },
        {
            name: "Intake",
            selector: "year",
            sortField: "intake",
            sortable: true,
            cell: (row) => (
                <span title={row.year && row.number ? `${row.year}-${row.number}` : ""}>
                    {row.year && row.number ? `${row.year}-${row.number}` : "-"}
                </span>
            ),
        },
        {
            name: "Type",
            selector: "subject_type",
            sortField: "subject_type",
            sortable: true,
            cell: (row) => (row.subject_type ? RenderCourseType(row.subject_type).html : "-"),
        },
        {
            name: "School",
            selector: "school_type",
            sortField: "school_type",
            sortable: true,
            cell: (row) => (row.school_type ? RenderSchool(row.school_type).html : "-"),
        },
        {
            name: "Actions",
            selector: "",
            cell: (row) => {
                const isPending = isCoursePending(row.id);

                return (
                <div className="assessment-08 btn-dropdown-grp">
                    <div className="as-buttons">
                        <PermissionsGate scopes={["casbadd"]} errorProps={{ disabled: true }}>
                            <button
                                type="button"
                                title={isPending ? "Remove" : "Add"}
                                className={`btn rounded-circle ${isPending ? "btn-danger" : "btn-primary"}`}
                                disabled={isLinking}
                                onClick={() => handleToggleCourse(row)}
                            >
                                <i className={`fal ${isPending ? "fa-minus" : "fa-plus"}`}></i>
                            </button>
                        </PermissionsGate>
                        <PermissionsGate
                            scopes={["capview"]}
                            RenderError={() => (
                                <button type="button" className="btn btn-primary rounded-circle" disabled title="Open">
                                    <i className="fal fa-folder-open"></i>
                                </button>
                            )}
                        >
                            <Link
                                className="btn btn-primary rounded-circle"
                                to={`/courseAdministration/coursesdetails/${row.id}/dashboard/show`}
                                title="Open"
                                target="_blank"
                            >
                                <i className="fal fa-folder-open"></i>
                            </Link>
                        </PermissionsGate>
                    </div>
                </div>
                );
            },
        },
    ], [isLinking, pendingIds]);

    const filters = [
        {
            filterName: "Status",
            optionArr: [
                { label: "Upcoming", value: CourseAdministrationTabNames.Upcoming },
                { label: "In Progress", value: CourseAdministrationTabNames.InProgress },
                { label: "Past", value: CourseAdministrationTabNames.Past },
            ],
            state: status,
            setState: setStatus,
            renderLabelFunction: RenderCourseStatus,
            singleSelect: true,
            uniqueId: MODAL_FILTER_UID,
        },
        {
            filterName: "Learning Method",
            optionArr: courseDropdown.learning || [],
            state: learningMethod,
            setState: setLearningMethod,
            renderLabelFunction: RenderLearningMethod,
            uniqueId: MODAL_FILTER_UID,
        },
        {
            filterName: "Intake",
            optionArr: courseDropdown.intake || [],
            state: intake,
            setState: setIntake,
            isOptionReversed: true,
            uniqueId: MODAL_FILTER_UID,
        },
        {
            filterName: "School",
            optionArr: courseDropdown.school || [],
            state: school,
            setState: setSchool,
            renderLabelFunction: RenderSchool,
            uniqueId: MODAL_FILTER_UID,
        },
        {
            filterName: "Type",
            optionArr: courseDropdown.type || [],
            state: courseType,
            setState: setCourseType,
            renderLabelFunction: RenderCourseType,
            uniqueId: MODAL_FILTER_UID,
        },
    ];

    return (
        <DataTableComponent
            data={courseList}
            loading={isLoading}
            state={tableState}
            setState={setTableState}
            setSearch={setSearch}
            totalRows={totalRows}
            columns={columns}
            filters={filters}
            isExport={false}
            isInsidePopUp={true}
            otherTableProps={{
                noDataComponent: emptyMessage || "There are no records to show",
            }}
        />
    );
};

export default AvailableCourse;
